import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { QuestionBase } from './question-base';
import { QuestionControlService } from '../../services/forms/question-control.service';
import { ProfileService } from 'core/services/profile.service';
import { ToastService } from 'core/services/toast.service';
import { FormQuestionsService } from 'core/services/form-questions-service';
import { StoreService } from 'core/services/store.service';
import { ConfigurationsService } from 'core/services/configurations.service';
import { LocalesService } from 'core/services/locales.service';
import { DEFAULT_CONFIG_FORM } from 'core/assets/constants';

@Component({
  selector: 'lms-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  providers: [QuestionControlService]
})
export class DynamicFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() questions: QuestionBase<any>[] = [];
  @Input() questionsService: FormQuestionsService;
  @Input() data: any;
  @Input() title: string;
  @Input() subtitle: string;
  @Input() action: string;
  @Input() options: any;
  @Input() containerClass?: string;
  @Output() submit = new EventEmitter<Object>();
  @Output() dataChanged = new EventEmitter<Object>();
  @Output() formSet = new EventEmitter<Object>();

  config: any;
  tooltipsMap: any;

  form: UntypedFormGroup;
  public formQuestionsSubscription: Subscription;
  public valueChangesSubscription: Subscription;
  private tooltipMessagesSubscription: Subscription = new Subscription();
  showSpinner = false;
  anyRequiredFields = false;

  public isFormEmpty = true;

  constructor(
    public qcs: QuestionControlService,
    public profileService: ProfileService,
    public toastService: ToastService,
    private configurationsService: ConfigurationsService,
    private localesService: LocalesService
  ) {}

  ngOnInit() {
    this.config = { ...DEFAULT_CONFIG_FORM, ...this.options };
    if (!this.profileService.canExecute(this.action)) {
      this.config.hideSubmit = true;
    }
    if (this.action && !StoreService.contains(this.action)) {
      this.tooltipMessagesSubscription = this.configurationsService
        .getInfoMessage$(
          'toolTip',
          'fields.keys[match]',
          this.action,
          'textoDeAyuda',
          this.localesService.getBrowserLocale()
        )
        .subscribe((tooltipsMap) => {
          StoreService.create(this.action, tooltipsMap);
        });
    }
  }

  ngOnChanges(changes) {
    if (this.questions && this.questions.length > 0) {
      this.setForm(this.questions);
    }
    if (this.questionsService) {
      this.formQuestionsSubscription = this.questionsService.getFormQuestions(this.data).subscribe((questions) => {
        if (questions) {
          this.setForm(questions);
        }
      });
    }
  }

  setForm(questions) {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }

    this.questions = questions;
    this.form = this.qcs.toFormGroup(this.questions, this.action);
    if (this.questionsService) {
      this.questionsService.setForm(this.form);
    }
    this.setAnyRequiredFields();

    this.valueChangesSubscription = this.form.valueChanges.subscribe((data) => {
      this.dataChanged.emit(data);
      if (this.questionsService) {
        this.questionsService.onDataChanged(data);
      }
    });

    this.isFormEmpty = !questions || !questions.length;
    this.formSet.emit({
      isFormEmpty: this.isFormEmpty
    });
  }

  ngOnDestroy() {
    this.tooltipMessagesSubscription.unsubscribe();
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
    if (this.formQuestionsSubscription) {
      this.formQuestionsSubscription.unsubscribe();
    }
  }

  onSubmit() {
    this.setSpinner(true);
    if (!this.form.valid) {
      this.submitted({ message: 'actions.anyErrorForm' });
    } else {
      const data = this.questionsService ? this.questionsService.getFormData() || this.form.value : this.form.value;
      this.submit.emit(data);
      this.form.markAsPristine();
    }
  }

  get submitDisabled() {
    return !this.config.isDirtySubmit
      ? this.form && (!this.form.valid || !this.form.dirty)
      : !this.config.isDirtySubmit;
  }

  get hasSections() {
    return this.questions ? this.questions.some((question) => question && question.controlType === 'group') : false;
  }

  setAnyRequiredFields() {
    const questions = this.questions || [];
    const someRequired = questions.some((question) => this.isRequiredQuestion(question));
    this.anyRequiredFields = someRequired;
  }

  isRequiredQuestion(question: QuestionBase<any>) {
    return (
      (question && question.validators && question.validators.required) ||
      (question.childQuestions &&
        question.childQuestions
          .filter((childQuestion) => childQuestion)
          .some((childQuestion) => this.isRequiredQuestion(childQuestion)))
    );
  }

  setSpinner(value) {
    if (this.config.showSpinnerUponSubmitting) {
      this.showSpinner = value;
    } else {
      this.showSpinner = false;
    }
  }

  submitted(err) {
    if (err) {
      this.toastService.showError(err, '', true);
    } else {
      this.toastService.showOk('', 3000, false);
    }
    window.scrollTo(0, 0);
    this.setSpinner(false);
  }
}
